import React from 'react'
import { Button, Input, Modal, Tag, Tooltip, Typography } from 'antd'
import { AxiosError } from 'axios'
import {
  ExclamationCircleOutlined,
  LockOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { sellerEmployeesApi } from 'api/sellerEmployees'
import { TEmployeeAccessSection } from 'models/sellerEmployees'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './EmployeeSectionAccess.module.scss'

const CODE_LENGTH = 4
const RESEND_AFTER_SEC = 60

const SECTION_NAMES: Record<TEmployeeAccessSection, string> = {
  BANKING: 'Банковские сведения',
  SHOP: 'Данные о магазине'
}

interface IEmployeeSectionAccessProps {
  section: TEmployeeAccessSection
  // ISO-время, до которого раздел открыт; null — закрыт
  until: string | null
  onGranted: (section: TEmployeeAccessSection, until: string) => void
}

const getErrorMessage = (e: unknown) =>
  (e as AxiosError<{ message: string }>).response?.data?.message

export const EmployeeSectionAccess: React.FC<IEmployeeSectionAccessProps> = ({
  section,
  until,
  onGranted
}) => {
  const [isCodeOpen, setIsCodeOpen] = React.useState(false)
  const [ownerPhone, setOwnerPhone] = React.useState('')
  const [isVerifying, setIsVerifying] = React.useState(false)
  const [error, setError] = React.useState('')
  const [resendAt, setResendAt] = React.useState(0)
  const [timer, setTimer] = React.useState(0)
  // ремонтируем поля кода, чтобы очистить их после неверной попытки
  const [codeFieldKey, setCodeFieldKey] = React.useState(0)

  React.useEffect(() => {
    if (!isCodeOpen) return

    const tick = () =>
      setTimer(Math.max(Math.ceil((resendAt - Date.now()) / 1000), 0))
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [isCodeOpen, resendAt])

  const openCodeModal = (phone: string, resendAfterSec: number) => {
    setOwnerPhone(phone)
    setResendAt(Date.now() + resendAfterSec * 1000)
    setError('')
    setCodeFieldKey((key) => key + 1)
    setIsCodeOpen(true)
  }

  const requestCode = async () => {
    try {
      const { data } = await sellerEmployeesApi.requestSectionAccess(section)
      openCodeModal(data.phone, data.resend_after_sec)
    } catch (e) {
      // код уже отправлен, а вкладка об этом не знает — даём его ввести
      if (getErrorMessage(e)?.includes('секунд')) {
        openCodeModal(ownerPhone, RESEND_AFTER_SEC)
      }
      showServerError(e)
    }
  }

  const showWarning = () => {
    Modal.confirm({
      title: 'Запросить доступ?',
      content: `Владельцу магазина будет отправлено SMS с кодом для подтверждения доступа. После ввода кода раздел «${SECTION_NAMES[section]}» откроется для редактирования на 1 час или до сохранения.`,
      icon: <ExclamationCircleOutlined />,
      okText: 'ОК',
      cancelText: 'Отмена',
      onOk: requestCode
    })
  }

  const verifyCode = async (code: string) => {
    try {
      setIsVerifying(true)
      setError('')
      const { data } = await sellerEmployeesApi.confirmSectionAccess(
        section,
        code
      )
      setIsCodeOpen(false)
      showMessage(
        'success',
        'Доступ открыт',
        'Раздел можно редактировать 1 час или до сохранения'
      )
      onGranted(section, data.until)
    } catch (e) {
      setError(getErrorMessage(e) ?? 'Неверный код подтверждения')
      setCodeFieldKey((key) => key + 1)
    } finally {
      setIsVerifying(false)
    }
  }

  return (
    <>
      {until ? (
        <span className={styles.granted}>
          <Tag color='success' className={styles.grantedTag}>
            Доступ открыт до {dayjs(until).format('HH:mm')}
          </Tag>
          <Tooltip title='Закроется раньше, если сохранить изменения'>
            <QuestionCircleOutlined className={styles.helpIcon} />
          </Tooltip>
        </span>
      ) : (
        <Button size='middle' icon={<LockOutlined />} onClick={showWarning}>
          Запросить доступ
        </Button>
      )}
      <Modal
        open={isCodeOpen}
        title='Код от владельца магазина'
        footer={null}
        onCancel={() => setIsCodeOpen(false)}
      >
        <div className={styles.codeWrapper}>
          <Typography.Text>
            Код отправлен по SMS владельцу магазина
            {ownerPhone ? ` на номер ${ownerPhone}` : ''}. Код действует 10
            минут.
          </Typography.Text>
          <Input.OTP
            key={codeFieldKey}
            length={CODE_LENGTH}
            disabled={isVerifying}
            status={error ? 'error' : undefined}
            formatter={(value) => value.replace(/\D/g, '')}
            onChange={verifyCode}
          />
          {error && <Typography.Text type='danger'>{error}</Typography.Text>}
          <Typography.Link
            disabled={timer > 0 || isVerifying}
            onClick={requestCode}
          >
            {timer > 0
              ? `Отправить код повторно можно через ${timer} сек`
              : 'Отправить код повторно'}
          </Typography.Link>
        </div>
      </Modal>
    </>
  )
}
